import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import LoadingSpinner from '../components/LoadingSpinner'
import ShoppingListForm from '../components/ShoppingListForm'
import { ShoppingList } from '../types'
import { shoppingListsApi } from '../services/api'
import toast from 'react-hot-toast'

const ShoppingListsPage = () => {
  const [lists, setLists] = useState<ShoppingList[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingList, setEditingList] = useState<ShoppingList | undefined>(undefined)

  useEffect(() => {
    loadLists()
  }, [])

  const loadLists = async () => {
    setIsLoading(true)
    try {
      const response = await shoppingListsApi.getLists()
      
      if (response.success && response.data) {
        // Backend returns lists directly in response.data, similar to products
        setLists(Array.isArray(response.data) ? response.data : [])
      } else {
        toast.error(response.error || 'Ostoslistojen lataus epäonnistui')
        setLists([])
      }
    } catch (error) {
      console.error('Failed to load shopping lists:', error)
      toast.error('Verkkovirhe')
      setLists([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateList = async (listData: Omit<ShoppingList, 'createdAt' | 'updatedAt'>) => {
    try {
      const response = await shoppingListsApi.createList({ list: listData })
      if (response.success) {
        toast.success('Ostoslista luotu')
        loadLists() // Reload lists
      } else {
        toast.error(response.error || 'Ostoslistan luonti epäonnistui')
        throw new Error(response.error)
      }
    } catch (error) {
      toast.error('Verkkovirhe')
      console.error('Create list error:', error)
      throw error
    }
  }

  const handleUpdateList = async (listData: Omit<ShoppingList, 'createdAt' | 'updatedAt'>) => {
    if (!editingList) return
    
    try {
      const response = await shoppingListsApi.updateList({
        listName: editingList.name,
        updates: listData
      })
      if (response.success) {
        toast.success('Ostoslista päivitetty')
        loadLists() // Reload lists
        setEditingList(undefined)
      } else {
        toast.error(response.error || 'Ostoslistan päivitys epäonnistui')
        throw new Error(response.error)
      }
    } catch (error) {
      toast.error('Verkkovirhe')
      console.error('Update list error:', error)
      throw error
    }
  }

  const handleDeleteList = async (listName: string) => {
    if (!confirm(`Haluatko varmasti poistaa ostoslistan "${listName}"?`)) {
      return
    }

    try {
      const response = await shoppingListsApi.deleteList(listName)
      if (response.success) {
        toast.success('Ostoslista poistettu')
        loadLists() // Reload lists
      } else {
        toast.error(response.error || 'Ostoslistan poisto epäonnistui')
      }
    } catch (error) {
      toast.error('Verkkovirhe')
      console.error('Delete list error:', error)
    }
  }

  const handleEditList = (list: ShoppingList) => {
    setEditingList(list)
    setIsFormOpen(true)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingList(undefined)
  }

  const calculateTotal = (list: ShoppingList) => {
    return list.items.reduce((total, item) => total + ((item.quantity || 0) * (item.unitPrice || 0)), 0)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Ostoslistat
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Luo ja hallitse ostoslistojasi
            </p>
          </div>
          <button 
            onClick={() => setIsFormOpen(true)}
            className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Uusi lista
          </button>
        </div>
      </div>

      {/* Shopping Lists */}
      {lists.length === 0 ? (
        <div className="bg-white shadow rounded-lg">
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Ei ostoslistoja</h3>
            <p className="mt-1 text-sm text-gray-500">Aloita luomalla ensimmäinen ostoslista.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {lists.map((list, index) => (
            <div key={index} className="bg-white shadow rounded-lg overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">{list.name}</h3>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleEditList(list)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button 
                      onClick={() => handleDeleteList(list.name)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {list.items.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">Tyhjä lista</p>
                  ) : (
                    <>
                      {list.items.slice(0, 3).map((item, itemIndex) => (
                        <div key={itemIndex} className="flex justify-between items-center text-sm">
                          <span className="text-gray-900">
                            {item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(1)} {item.quantityUnit || 'kpl'} {item.name}
                          </span>
                          <span className="text-gray-600">
                            {((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)} €
                          </span>
                        </div>
                      ))}
                      {list.items.length > 3 && (
                        <p className="text-sm text-gray-500">
                          ... ja {list.items.length - 3} muuta tuotetta
                        </p>
                      )}
                    </>
                  )}
                </div>
                
                {list.items.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-900">Yhteensä:</span>
                      <span className="text-lg font-bold text-primary-600">
                        {calculateTotal(list).toFixed(2)} €
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>
                      {list.items.length} tuotetta
                    </span>
                    <span>
                      {list.updatedAt ? 
                        `Päivitetty ${new Date(list.updatedAt).toLocaleDateString('fi-FI')}` :
                        `Luotu ${new Date(list.createdAt!).toLocaleDateString('fi-FI')}`
                      }
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-6 py-3">
                <Link 
                  to={`/shopping-lists/${encodeURIComponent(list.name)}`}
                  className="block w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Avaa lista →
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Shopping List Form Modal */}
      <ShoppingListForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSave={editingList ? handleUpdateList : handleCreateList}
        list={editingList}
        isEditing={!!editingList}
      />
    </div>
  )
}

export default ShoppingListsPage 